import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePurchaseManager } from "../_stubs/usePurchaseManager";
import { useCreditManager } from "../_stubs/useCreditManager";
import { useHintPointsManager } from "../_stubs/useHintPointsManager";
import { AUTH_CHANGED_EVENT } from "../../../../services/AuthService";
import {
    getRevenueCatEntitlementStatus,
    isRevenueCatEnabled,
} from "../_stubs/revenueCatPurchase";
import { loadPersistedStandardAccessFlag } from "../_stubs/SubscriptionManager";
import { isConfigurationLocked as isConfigurationLockedCore } from "./subscriptionManager";
import { isSnapshotUiTesting, useMobileSlidingAccess } from "./access";

const SubscriptionContext = createContext(null);

/**
 * App-wide subscription state for `/mobile/sliding/*` (React equivalent of Swift `@EnvironmentObject SubscriptionManager`).
 *
 * Wrap once in `SlidingMasterLayout`:
 * ```jsx
 * <SubscriptionProvider>
 *   <Outlet /> or {children}
 * </SubscriptionProvider>
 * ```
 *
 * Consume in any child:
 * ```jsx
 * const { isSubscribed, isConfigurationLocked, subscribe, restore } = useSubscription();
 * ```
 */
export function SubscriptionProvider({ children }) {
    const access = useMobileSlidingAccess();
    const iap = usePurchaseManager();
    const credits = useCreditManager();
    const hintPoints = useHintPointsManager();
    const [rcEntitlementActive, setRcEntitlementActive] = useState(false);
    const [localStandardAccess, setLocalStandardAccess] = useState(false);

    const refreshLocalStandardAccess = useCallback(async () => {
        try {
            setLocalStandardAccess(await loadPersistedStandardAccessFlag());
        } catch {
            setLocalStandardAccess(false);
        }
    }, []);

    const refreshRevenueCatEntitlement = useCallback(async () => {
        if (!isRevenueCatEnabled()) {
            setRcEntitlementActive(false);
            return;
        }
        try {
            const { active } = await getRevenueCatEntitlementStatus();
            setRcEntitlementActive(active);
        } catch {
            setRcEntitlementActive(false);
        }
    }, []);

    useEffect(() => {
        if (isSnapshotUiTesting()) {
            setRcEntitlementActive(false);
            setLocalStandardAccess(false);
            return;
        }
        void refreshRevenueCatEntitlement();
        void refreshLocalStandardAccess();
    }, [refreshRevenueCatEntitlement, refreshLocalStandardAccess, access.loading, iap.isPremium]);

    useEffect(() => {
        const onAuth = () => {
            void refreshRevenueCatEntitlement();
            void credits.refreshBalance();
            void hintPoints.refreshBalance();
        };
        window.addEventListener(AUTH_CHANGED_EVENT, onAuth);
        return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuth);
    }, [refreshRevenueCatEntitlement, credits, hintPoints]);

    /** Credit wallet, legacy cloud Standard plan, backend IAP sync, on-device receipt, or RevenueCat entitlement. */
    const hasStandardPlan =
        !access.loading &&
        (credits.hasCredits
            || access.hasStandardPlan
            || access.isPremium
            || iap.isPremium
            || rcEntitlementActive
            || localStandardAccess);
    const isSubscribed = hasStandardPlan;

    const isConfigurationLocked = useCallback(
        (gridSize, difficulty) => {
            if (access.loading) return isConfigurationLockedCore(gridSize, difficulty, false);
            return isConfigurationLockedCore(gridSize, difficulty, isSubscribed);
        },
        [access.loading, isSubscribed]
    );

    const notifyAccessRefresh = useCallback(() => {
        if (typeof window !== "undefined") {
            window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
        }
        void iap.refreshPremium();
        void credits.refreshAll();
        void hintPoints.refreshAll();
        void refreshRevenueCatEntitlement();
        void refreshLocalStandardAccess();
    }, [iap, credits, hintPoints, refreshRevenueCatEntitlement, refreshLocalStandardAccess]);

    const purchaseCredits = useCallback(
        (packageKey) => credits.purchase(packageKey),
        [credits],
    );

    const purchaseHintPoints = useCallback(() => hintPoints.purchase(), [hintPoints]);

    /**
     * Cloud Standard / email premium without a credit wallet — not Master Pass IAP (same receipt as credits).
     * Requires a successful balance fetch so a failed wallet load is not treated as unlimited play.
     */
    const hasLegacyUnlimitedAccess =
        !access.loading &&
        !credits.balanceLoading &&
        credits.balanceLoaded &&
        access.isAuthenticated &&
        (access.hasStandardPlan || access.isPremium) &&
        !credits.hasCredits;

    const value = useMemo(
        () => ({
            access,
            iap,
            credits,
            hintPoints,
            isSubscribed,
            hasStandardPlan,
            hasLegacyUnlimitedAccess,
            isConfigurationLocked,
            isIapAvailable: credits.isIapAvailable || hintPoints.isIapAvailable || iap.isIapAvailable,
            isProcessing: iap.isProcessing || credits.isProcessing || hintPoints.isProcessing,
            subscribe: iap.subscribe,
            restore: iap.restore,
            purchaseCredits,
            purchaseHintPoints,
            notifyAccessRefresh,
        }),
        [
            access,
            iap,
            credits,
            hintPoints,
            isSubscribed,
            hasStandardPlan,
            hasLegacyUnlimitedAccess,
            isConfigurationLocked,
            purchaseCredits,
            purchaseHintPoints,
            notifyAccessRefresh,
        ],
    );

    return (
        <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
    );
}

export function useSubscription() {
    const ctx = useContext(SubscriptionContext);
    if (!ctx) {
        throw new Error("useSubscription must be used within SubscriptionProvider");
    }
    return ctx;
}
